import { Alert, Button } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import { faArrowDown, faArrowUp, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FormCheckbox, FormLabel, Input, toasty,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Modal } from '../common/Modal';
import { InputTypeIds } from '../formsAndSurveys/FormsAndSurveys';

export const questionTypeOptions = [
    { value: InputTypeIds.Text, label: 'Long answer' },
    { value: InputTypeIds.SingleLineText, label: 'Short answer' },
    { value: InputTypeIds.Select, label: 'Multiple choice' },
];

/** Rows created in the browser carry a uuid until they are saved and get a database id. */
export const isUnsaved = (id) => typeof id === 'string';

export const questionTypeLabel = (question) => questionTypeOptions
    .find((x) => x.value === question.inputTypeId)?.label ?? question.inputTypeName ?? '';

export const newChargeQuestion = (order) => ({
    id: uuid(),
    name: '',
    subText: '',
    inputTypeId: InputTypeIds.Text,
    answerRequired: false,
    showDuringReview: true,
    order,
    linkedChargeTypeCount: 0,
    hasAnswers: false,
    options: [],
});

const getValidationMessage = (question, activeOptions) => {
    if (!question.name?.trim()) return 'Enter the question text.';

    if (question.inputTypeId !== InputTypeIds.Select) return null;

    const choices = activeOptions.map((o) => (o.choiceText ?? '').trim());
    if (choices.length === 0) return 'Add at least one option.';
    if (choices.some((c) => !c)) return 'Every option needs text.';
    if (new Set(choices.map((c) => c.toLowerCase())).size !== choices.length) return 'Each option must be different.';

    return null;
};

const ChargeQuestionEditorBody = ({ question, values, setFieldValue }) => {
    const isMultipleChoice = values.inputTypeId === InputTypeIds.Select;
    const isEditableType = questionTypeOptions.some((x) => x.value === question.inputTypeId);
    const activeOptions = values.options.filter((o) => o.isActive);
    const removedOptions = values.options.filter((o) => !o.isActive);

    const setActiveOptions = (nextActive) => setFieldValue('options', [...nextActive, ...removedOptions]);

    const updateOption = (id, changes) => setFieldValue(
        'options',
        values.options.map((o) => (o.id === id ? { ...o, ...changes } : o)),
    );

    const addOption = () => setActiveOptions([
        ...activeOptions,
        {
            id: uuid(), choiceText: '', isActive: true, order: activeOptions.length,
        },
    ]);

    // A saved option may already be someone's answer, so it is set aside rather than deleted.
    const removeOption = (option) => {
        if (isUnsaved(option.id)) {
            setActiveOptions(activeOptions.filter((o) => o.id !== option.id));
        } else {
            updateOption(option.id, { isActive: false });
        }
    };

    const moveOption = (index, offset) => {
        const next = [...activeOptions];
        [next[index], next[index + offset]] = [next[index + offset], next[index]];
        setActiveOptions(next);
    };

    return (
        <>
            {question.linkedChargeTypeCount > 1 && (
                <Alert color="info">
                    {`Used by ${question.linkedChargeTypeCount} charge types — changes apply to all of them.`}
                </Alert>
            )}

            <Input
                id="name"
                name="name"
                label="Question"
                required
                error="Enter the question text."
            />

            <Input
                id="subText"
                name="subText"
                label="Sub text"
                helperText="Shown under the question to help crews answer it."
            />

            <ValidatedSelect
                id="inputTypeId"
                name="inputTypeId"
                label="Type"
                asPrimitive
                hideClear
                required
                isDisabled={!isEditableType || question.hasAnswers}
                options={isEditableType
                    ? questionTypeOptions
                    : [{ value: question.inputTypeId, label: questionTypeLabel(question) }]}
                helperText={question.hasAnswers ? 'This question has been answered, so its type can no longer change.' : ''}
            />

            <Input
                id="order"
                name="order"
                label="Order"
                type="number"
                min={0}
                required
                error="Enter the order."
                helperText="Position of this question for this charge type."
            />

            <FormCheckbox
                id="answerRequired"
                name="answerRequired"
                labelText="Required"
            />

            <FormCheckbox
                id="showDuringReview"
                name="showDuringReview"
                labelText="Show during review"
                className="mb-3"
            />

            {isMultipleChoice && (
                <>
                    <div className="d-flex align-items-baseline">
                        <FormLabel
                            className="control-label"
                            label="Options"
                        />
                        <Button
                            color="outline-primary"
                            size="sm"
                            className="ml-auto"
                            onClick={addOption}
                        >
                            <FontAwesomeIcon
                                icon={faPlusCircle}
                                className="mr-2"
                            />
                            Add Option
                        </Button>
                    </div>

                    {activeOptions.map((option, index) => (
                        <div
                            key={option.id}
                            className="d-flex align-items-center mb-2"
                        >
                            <Input
                                aria-label={`Option ${index + 1}`}
                                formGroupClassName="flex-grow-1"
                                required
                                error="Enter the option text."
                                value={option.choiceText ?? ''}
                                onChange={(e) => updateOption(option.id, { choiceText: e.target.value })}
                            />
                            <Button
                                color="link"
                                size="sm"
                                title="Move up"
                                disabled={index === 0}
                                onClick={() => moveOption(index, -1)}
                            >
                                <FontAwesomeIcon icon={faArrowUp} />
                            </Button>
                            <Button
                                color="link"
                                size="sm"
                                title="Move down"
                                disabled={index === activeOptions.length - 1}
                                onClick={() => moveOption(index, 1)}
                            >
                                <FontAwesomeIcon icon={faArrowDown} />
                            </Button>
                            <Button
                                color="link"
                                size="sm"
                                onClick={() => removeOption(option)}
                            >
                                Remove
                            </Button>
                        </div>
                    ))}

                    {removedOptions.length > 0 && (
                        <>
                            <FormLabel
                                className="control-label mt-2 mb-1 text-muted"
                                label="Removed options"
                            />
                            <small className="d-block text-muted mb-1">
                                Hidden from new entries; answers that already use them still show them.
                            </small>
                            {removedOptions.map((option) => (
                                <div
                                    key={option.id}
                                    className="d-flex align-items-center"
                                >
                                    <span className="text-muted">{option.choiceText}</span>
                                    <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => setActiveOptions([...activeOptions, { ...option, isActive: true }])}
                                    >
                                        Restore
                                    </Button>
                                </div>
                            ))}
                        </>
                    )}
                </>
            )}
        </>
    );
};

export const showChargeQuestionEditor = (question, onSave) => Modal.show({
    title: isUnsaved(question.id) ? 'New Question' : 'Edit Question',
    size: 'lg',
    initialValues: {
        ...question,
        name: question.name ?? '',
        subText: question.subText ?? '',
        order: question.order ?? 0,
        options: (question.options ?? []).map((o) => ({ ...o })),
    },
    SaveButtonProps: { label: 'Done' },
    body: (props) => (
        <ChargeQuestionEditorBody
            {...props}
            question={question}
        />
    ),
    onSubmit: (values) => {
        const isMultipleChoice = values.inputTypeId === InputTypeIds.Select;
        const activeOptions = values.options.filter((o) => o.isActive);
        const message = getValidationMessage(values, activeOptions);
        if (message) {
            toasty.error(message);
            return { keepOpen: true };
        }

        onSave({
            ...values,
            name: values.name.trim(),
            options: isMultipleChoice
                ? [...activeOptions.map((o, i) => ({ ...o, order: i })), ...values.options.filter((o) => !o.isActive)]
                : values.options,
        });

        return undefined;
    },
});
